import { useState, useEffect, useRef } from 'react'

interface SettingsProps {
  onClose: () => void
  language: 'FR' | 'AR'
  onLanguageChange: (lang: 'FR' | 'AR') => void
  user?: { email?: string; name?: string } | null
  onDeleteAllHistory?: () => void
  openConfirmModal?: (message: string, onConfirm: () => void, confirmButtonText?: string) => void
  openDeleteAccountModal?: (onConfirm: () => void) => void
  theme?: 'light' | 'dark'
  onThemeChange?: (theme: 'light' | 'dark') => void
}

function Settings({ onClose, language, onLanguageChange, user, onDeleteAllHistory, openConfirmModal, openDeleteAccountModal, theme = 'light', onThemeChange }: SettingsProps) {
  const [activeSection, setActiveSection] = useState('general')
  // Track which theme button is selected (for UI display)
  // Initialize based on current theme: if dark, show dark selected; otherwise show light selected
  const [selectedTheme, setSelectedTheme] = useState<'light' | 'dark' | 'system'>(theme === 'dark' ? 'dark' : 'light')
  
  // Update selectedTheme when theme prop changes from outside
  useEffect(() => {
    if (theme === 'dark') {
      setSelectedTheme('dark')
    } else {
      // If theme is light, keep current selection (could be 'light' or 'system')
      // Don't change it unless it was 'dark'
    }
  }, [theme])
  const [showLanguageMenu, setShowLanguageMenu] = useState(false)
  const [showSubscriptionTooltip, setShowSubscriptionTooltip] = useState(false)
  const [activeTooltipButton, setActiveTooltipButton] = useState<'upgrade' | 'manage' | 'billing' | null>(null)
  const subscriptionTooltipRef = useRef<HTMLDivElement>(null)
  const upgradeButtonRef = useRef<HTMLButtonElement>(null)
  const manageButtonRef = useRef<HTMLButtonElement>(null)
  const billingHelpRef = useRef<HTMLButtonElement>(null)

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [onClose])

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [])

  // Auto-dismiss subscription tooltip after 3 seconds
  useEffect(() => {
    if (showSubscriptionTooltip) {
      const timer = setTimeout(() => {
        setShowSubscriptionTooltip(false)
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [showSubscriptionTooltip])

  // Close subscription tooltip when clicking outside or scrolling
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showSubscriptionTooltip) {
        const target = event.target as Node
        if (
          upgradeButtonRef.current &&
          !upgradeButtonRef.current.contains(target) &&
          manageButtonRef.current &&
          !manageButtonRef.current.contains(target) &&
          billingHelpRef.current &&
          !billingHelpRef.current.contains(target) &&
          subscriptionTooltipRef.current &&
          !subscriptionTooltipRef.current.contains(target)
        ) {
          setShowSubscriptionTooltip(false)
        }
      }
    }
    const handleScroll = () => {
      if (showSubscriptionTooltip) {
        setShowSubscriptionTooltip(false)
      }
    }
    if (showSubscriptionTooltip) {
      document.addEventListener('mousedown', handleClickOutside)
      window.addEventListener('scroll', handleScroll, true)
      window.addEventListener('resize', handleScroll)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
        window.removeEventListener('scroll', handleScroll, true)
        window.removeEventListener('resize', handleScroll)
      }
    }
  }, [showSubscriptionTooltip])

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.45)' }}
        onClick={onClose}
      >
        {/* Modal Container */}
        <div 
          className={`rounded-2xl shadow-2xl w-full max-w-[760px] flex flex-col select-none transition-colors duration-200 ${
            theme === 'dark' ? 'bg-[#1A1A1A]' : 'bg-white'
          }`}
          onClick={(e) => e.stopPropagation()}
          style={{ borderRadius: '16px', minHeight: '520px', maxHeight: '72vh', transform: 'translateX(8px)' }}
        >
          {/* Modal Content */}
          <div className="flex flex-1 min-h-0 py-8 pl-8">
            {/* Left Sidebar */}
            <div className={`w-56 border-r pr-6 flex flex-col ${
              theme === 'dark' ? 'border-[#2C2C2C]' : 'border-gray-200'
            }`}>
              <nav className="space-y-1.5">
                <button
                  onClick={() => setActiveSection('general')}
                  className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-colors duration-150 ${
                    activeSection === 'general'
                      ? theme === 'dark'
                        ? 'bg-[#2C2C2C] text-white font-medium'
                        : 'bg-gray-100 text-gray-900 font-medium'
                      : theme === 'dark'
                        ? 'text-gray-400 hover:bg-[#2C2C2C]'
                        : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  Général
                </button>

                <button
                  onClick={() => setActiveSection('profil')}
                  className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-colors duration-150 ${
                    activeSection === 'profil'
                      ? theme === 'dark'
                        ? 'bg-[#2C2C2C] text-white font-medium'
                        : 'bg-gray-100 text-gray-900 font-medium'
                      : theme === 'dark'
                        ? 'text-gray-400 hover:bg-[#2C2C2C]'
                        : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  Profil
                </button>

                <button
                  onClick={() => setActiveSection('nouveautes')}
                  className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-colors duration-150 ${
                    activeSection === 'nouveautes'
                      ? theme === 'dark'
                        ? 'bg-[#2C2C2C] text-white font-medium'
                        : 'bg-gray-100 text-gray-900 font-medium'
                      : theme === 'dark'
                        ? 'text-gray-400 hover:bg-[#2C2C2C]'
                        : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  Nouveautés
                </button>

                <button
                  onClick={() => setActiveSection('about')}
                  className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-colors duration-150 ${
                    activeSection === 'about'
                      ? theme === 'dark'
                        ? 'bg-[#2C2C2C] text-white font-medium'
                        : 'bg-gray-100 text-gray-900 font-medium'
                      : theme === 'dark'
                        ? 'text-gray-400 hover:bg-[#2C2C2C]'
                        : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  À propos
                </button>
              </nav>
            </div>

            {/* Right Content Area */}
            <div className="flex-1 pl-8 overflow-y-auto flex flex-col relative">
              {/* Close Button */}
              <button
                onClick={onClose}
                className={`absolute top-5 right-5 w-8 h-8 rounded-lg flex items-center justify-center transition-colors duration-200 z-10 ${
                  theme === 'dark'
                    ? 'hover:bg-[#2C2C2C] text-gray-400 hover:text-gray-300'
                    : 'hover:bg-gray-100 text-gray-400 hover:text-gray-600'
                }`}
                title="Fermer"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              {/* Header */}
              <div className="mb-8 pr-8">
                <h1 className={`text-xl font-semibold mb-2.5 ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>Paramètres</h1>
                <p className={`text-sm ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  Gérez vos préférences et informations de compte
                </p>
              </div>

              {/* Content */}
              <div className="flex-1 flex items-start justify-center pr-8">
                <div className="w-full max-w-[540px] space-y-8">
                  {activeSection === 'general' && (
                    <>
                      {/* Theme Section */}
                      <div>
                        <h3 className={`text-sm font-semibold mb-3 ${
                          theme === 'dark' ? 'text-white' : 'text-gray-900'
                        }`}>Thème</h3>
                        <div className="grid grid-cols-3 gap-3">
                          {/* Light Theme */}
                          <button
                            onClick={() => {
                              setSelectedTheme('light')
                              if (onThemeChange) {
                                onThemeChange('light')
                              }
                            }}
                            className={`p-4 rounded-lg border-2 transition-all duration-150 ${
                              selectedTheme === 'light'
                                ? theme === 'dark'
                                  ? 'border-[#3ECF8E] bg-[#3ECF8E]/20'
                                  : 'border-green-600 bg-green-50'
                                : theme === 'dark'
                                  ? 'border-[#2C2C2C] bg-[#2C2C2C] hover:border-[#3C3C3C]'
                                  : 'border-gray-200 bg-white hover:border-gray-300'
                            }`}
                          >
                            <div className="flex flex-col items-center gap-2">
                              <svg className={`w-6 h-6 ${
                                theme === 'dark'
                                  ? selectedTheme === 'light' ? 'text-[#3ECF8E]' : 'text-gray-400'
                                  : 'text-gray-700'
                              }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                              </svg>
                              <span className={`text-sm font-medium ${
                                theme === 'dark'
                                  ? selectedTheme === 'light' ? 'text-[#3ECF8E]' : 'text-gray-300'
                                  : 'text-gray-700'
                              }`}>Clair</span>
                            </div>
                          </button>

                          {/* Dark Theme */}
                          <button
                            onClick={() => {
                              setSelectedTheme('dark')
                              if (onThemeChange) {
                                onThemeChange('dark')
                              }
                            }}
                            className={`p-4 rounded-lg border-2 transition-all duration-150 ${
                              selectedTheme === 'dark'
                                ? theme === 'dark'
                                  ? 'border-[#3ECF8E] bg-[#3ECF8E]/20'
                                  : 'border-green-600 bg-green-50'
                                : theme === 'dark'
                                  ? 'border-[#2C2C2C] bg-[#2C2C2C] hover:border-[#3C3C3C]'
                                  : 'border-gray-200 bg-white hover:border-gray-300'
                            }`}
                          >
                            <div className="flex flex-col items-center gap-2">
                              <svg className={`w-6 h-6 ${
                                theme === 'dark'
                                  ? selectedTheme === 'dark' ? 'text-[#3ECF8E]' : 'text-gray-400'
                                  : 'text-gray-700'
                              }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                              </svg>
                              <span className={`text-sm font-medium ${
                                theme === 'dark'
                                  ? selectedTheme === 'dark' ? 'text-[#3ECF8E]' : 'text-gray-300'
                                  : 'text-gray-700'
                              }`}>Sombre</span>
                            </div>
                          </button>

                          {/* System Theme */}
                          <button
                            onClick={() => {
                              setSelectedTheme('system')
                              // System theme should set to light mode as per user request
                              if (onThemeChange) {
                                onThemeChange('light')
                              }
                            }}
                            className={`p-4 rounded-lg border-2 transition-all duration-150 ${
                              selectedTheme === 'system'
                                ? theme === 'dark'
                                  ? 'border-[#3ECF8E] bg-[#3ECF8E]/20'
                                  : 'border-green-600 bg-green-50'
                                : theme === 'dark'
                                  ? 'border-[#2C2C2C] bg-[#2C2C2C] hover:border-[#3C3C3C]'
                                  : 'border-gray-200 bg-white hover:border-gray-300'
                            }`}
                          >
                            <div className="flex flex-col items-center gap-2">
                              <svg className={`w-6 h-6 ${
                                theme === 'dark'
                                  ? selectedTheme === 'system' ? 'text-[#3ECF8E]' : 'text-gray-400'
                                  : 'text-gray-700'
                              }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                              </svg>
                              <span className={`text-sm font-medium ${
                                theme === 'dark'
                                  ? selectedTheme === 'system' ? 'text-[#3ECF8E]' : 'text-gray-300'
                                  : 'text-gray-700'
                              }`}>Système</span>
                            </div>
                          </button>
                        </div>
                      </div>

                      {/* Language Section */}
                      <div>
                        <h3 className={`text-sm font-semibold mb-3 ${
                          theme === 'dark' ? 'text-white' : 'text-gray-900'
                        }`}>Langue</h3>
                        <div className="relative">
                          {/* Selected Language Display */}
                          <button 
                            onClick={() => setShowLanguageMenu(!showLanguageMenu)}
                            className={`w-full flex items-center justify-between p-4 rounded-lg transition-colors duration-150 ${
                              theme === 'dark'
                                ? 'bg-[#2C2C2C] border border-[#3C3C3C] hover:border-[#4C4C4C]'
                                : 'bg-white border border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <span className={`px-2 py-1 rounded text-xs font-semibold ${
                                theme === 'dark'
                                  ? 'bg-[#1A1A1A] text-gray-300'
                                  : 'bg-gray-100 text-gray-600'
                              }`}>
                                {language === 'FR' ? 'FR' : 'AR'}
                              </span>
                              <span className={`text-sm font-medium ${
                                theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
                              }`}>
                                {language === 'FR' ? 'Français' : 'العربية'}
                              </span>
                            </div>
                            <svg 
                              className={`w-4 h-4 transition-transform duration-200 ${showLanguageMenu ? 'rotate-180' : ''} ${
                                theme === 'dark' ? 'text-gray-400' : 'text-gray-400'
                              }`} 
                              fill="none" 
                              stroke="currentColor" 
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>

                          {/* Language Options Dropdown */}
                          {showLanguageMenu && (
                            <div className={`absolute z-10 w-full mt-2 rounded-lg shadow-lg overflow-hidden ${
                              theme === 'dark'
                                ? 'bg-[#2C2C2C] border border-[#3C3C3C]'
                                : 'bg-white border border-gray-200'
                            }`}>
                              {language !== 'AR' && (
                                <button 
                                  onClick={() => {
                                    onLanguageChange('AR')
                                    setShowLanguageMenu(false)
                                  }}
                                  className={`w-full flex items-center gap-3 p-4 transition-colors duration-150 ${
                                    theme === 'dark'
                                      ? 'hover:bg-[#3C3C3C]'
                                      : 'hover:bg-gray-50'
                                  }`}
                                >
                                  <span className={`px-2 py-1 rounded text-xs font-semibold ${
                                    theme === 'dark'
                                      ? 'bg-[#1A1A1A] text-gray-300'
                                      : 'bg-gray-100 text-gray-600'
                                  }`}>AR</span>
                                  <span className={`text-sm font-medium ${
                                    theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
                                  }`}>العربية</span>
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  )}

                  {activeSection === 'profil' && (
                    <div className="space-y-6">
                      {/* User Information */}
                      <div>
                        <h3 className={`text-sm font-semibold mb-3 ${
                          theme === 'dark' ? 'text-white' : 'text-gray-900'
                        }`}>Informations utilisateur</h3>
                        <div className={`rounded-lg p-4 ${
                          theme === 'dark'
                            ? 'bg-[#2C2C2C] border border-[#3C3C3C]'
                            : 'bg-white border border-gray-200'
                        }`}>
                          <div className="space-y-2">
                            <div>
                              <p className={`text-sm font-medium ${
                                theme === 'dark' ? 'text-white' : 'text-gray-900'
                              }`}>
                                {user?.name 
                                  ? user.name.split(' ')[0] 
                                  : user?.email 
                                    ? user.email.split('@')[0] 
                                    : 'Utilisateur'}
                              </p>
                            </div>
                            <div>
                              <p className={`text-sm ${
                                theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
                              }`}>
                                {user?.email || 'utilisateur@example.com'}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Subscription */}
                      <div>
                        <h3 className={`text-sm font-semibold mb-3 ${
                          theme === 'dark' ? 'text-white' : 'text-gray-900'
                        }`}>Abonnement</h3>
                        <div className={`rounded-lg p-4 ${
                          theme === 'dark'
                            ? 'bg-[#2C2C2C] border border-[#3C3C3C]'
                            : 'bg-white border border-gray-200'
                        }`}>
                          <div className="flex items-start justify-between">
                            <div>
                              <div className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium mb-2 ${
                                theme === 'dark'
                                  ? 'bg-[#1A1A1A] text-gray-300'
                                  : 'bg-gray-100 text-gray-800'
                              }`}>
                                Gratuit
                              </div>
                              <p className={`text-sm ${
                                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                              }`}>
                                Vous profitez actuellement de l'essai gratuit
                              </p>
                            </div>
                            <button 
                              ref={manageButtonRef}
                              onClick={() => {
                                setActiveTooltipButton('manage')
                                setShowSubscriptionTooltip(true)
                              }}
                              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-150 ${
                                theme === 'dark'
                                  ? 'bg-[#2C2C2C] border border-[#3C3C3C] text-gray-300 hover:bg-[#3C3C3C]'
                                  : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                              }`}
                            >
                              Gérer
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Plan Change */}
                      <div>
                        <h3 className={`text-sm font-semibold mb-3 ${
                          theme === 'dark' ? 'text-white' : 'text-gray-900'
                        }`}>Changement de formule</h3>
                        <button 
                          ref={upgradeButtonRef}
                          onClick={() => {
                            setActiveTooltipButton('upgrade')
                            setShowSubscriptionTooltip(true)
                          }}
                          className="w-auto flex items-center justify-center gap-2.5 px-5 py-3 bg-[#3ECF8E] hover:bg-[#34B27B] text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                          </svg>
                          <span className="text-sm">Passer au forfait supérieur</span>
                        </button>
                      </div>

                      {/* Billing Help */}
                      <div>
                        <button 
                          ref={billingHelpRef}
                          onClick={() => {
                            setActiveTooltipButton('billing')
                            setShowSubscriptionTooltip(true)
                          }}
                          className={`text-sm transition-colors duration-150 ${
                            theme === 'dark'
                              ? 'text-white hover:text-gray-300'
                              : 'text-gray-500 hover:text-gray-700'
                          }`}
                        >
                          Besoin d'aide pour la facturation ?
                        </button>
                      </div>

                      {/* Sensitive Actions */}
                      <div className={`pt-4 border-t ${
                        theme === 'dark' ? 'border-[#2C2C2C]' : 'border-gray-200'
                      }`}>
                        <h3 className={`text-sm font-semibold mb-3 ${
                          theme === 'dark' ? 'text-white' : 'text-gray-900'
                        }`}>Actions sensibles</h3>
                        <div className="space-y-2">
                          <button 
                            onClick={() => {
                              if (openConfirmModal && onDeleteAllHistory) {
                                openConfirmModal(
                                  'Êtes-vous sûr de vouloir supprimer tout l\'historique ? Cette action supprimera toutes vos conversations et tous les documents du Vault. Cette action est irréversible.',
                                  () => {
                                    onDeleteAllHistory()
                                    onClose()
                                  },
                                  'Supprimer'
                                )
                              }
                            }}
                            className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors duration-150 group ${
                              theme === 'dark'
                                ? 'bg-[#2C2C2C] border border-[#3C3C3C] hover:border-red-500/50 hover:bg-red-500/10'
                                : 'bg-white border border-gray-200 hover:border-red-300 hover:bg-red-50'
                            }`}
                          >
                            <span className={`text-sm group-hover:text-red-700 ${
                              theme === 'dark'
                                ? 'text-gray-300 group-hover:text-red-400'
                                : 'text-gray-700'
                            }`}>Supprimer l'historique</span>
                            <svg className={`w-4 h-4 group-hover:text-red-500 ${
                              theme === 'dark' ? 'text-gray-400' : 'text-gray-400'
                            }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                          <button 
                            onClick={() => {
                              if (openDeleteAccountModal) {
                                openDeleteAccountModal(() => {
                                  onClose()
                                })
                              }
                            }}
                            className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors duration-150 group ${
                              theme === 'dark'
                                ? 'bg-[#2C2C2C] border border-[#3C3C3C] hover:border-red-500/50 hover:bg-red-500/10'
                                : 'bg-white border border-gray-200 hover:border-red-300 hover:bg-red-50'
                            }`}
                          >
                            <span className={`text-sm group-hover:text-red-700 ${
                              theme === 'dark'
                                ? 'text-gray-300 group-hover:text-red-400'
                                : 'text-gray-700'
                            }`}>Supprimer le compte</span>
                            <svg className={`w-4 h-4 group-hover:text-red-500 ${
                              theme === 'dark' ? 'text-gray-400' : 'text-gray-400'
                            }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeSection === 'about' && (
                    <div className="space-y-3">
                      <a 
                        href="/terms"
                        className={`w-full flex items-center justify-between p-4 rounded-lg transition-colors duration-150 ${
                          theme === 'dark'
                            ? 'bg-[#2C2C2C] border border-[#3C3C3C] hover:border-[#4C4C4C] hover:bg-[#3C3C3C]'
                            : 'bg-white border border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <span className={`text-sm ${
                          theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
                        }`}>Conditions d'utilisation</span>
                        <svg className={`w-4 h-4 ${
                          theme === 'dark' ? 'text-gray-400' : 'text-gray-400'
                        }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </a>

                      <a 
                        href="/privacy"
                        className={`w-full flex items-center justify-between p-4 rounded-lg transition-colors duration-150 ${
                          theme === 'dark'
                            ? 'bg-[#2C2C2C] border border-[#3C3C3C] hover:border-[#4C4C4C] hover:bg-[#3C3C3C]'
                            : 'bg-white border border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <span className={`text-sm ${
                          theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
                        }`}>Politique de confidentialité</span>
                        <svg className={`w-4 h-4 ${
                          theme === 'dark' ? 'text-gray-400' : 'text-gray-400'
                        }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </a>
                    </div>
                  )}

                  {activeSection === 'nouveautes' && (
                    <div className="space-y-6">
                      {/* Notifications Placeholder */}
                      <div className="text-center py-12">
                        <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
                          <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                          </svg>
                        </div>
                        <h3 className="text-base font-semibold text-gray-900 mb-2">Aucune nouveauté pour le moment</h3>
                        <p className="text-sm text-gray-500 max-w-sm mx-auto">
                          Les nouvelles fonctionnalités et mises à jour importantes apparaîtront ici
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Subscription Tooltip Bubble */}
      {showSubscriptionTooltip && (() => {
        let buttonRef: HTMLButtonElement | null = null
        if (activeTooltipButton === 'upgrade' && upgradeButtonRef.current) {
          buttonRef = upgradeButtonRef.current
        } else if (activeTooltipButton === 'manage' && manageButtonRef.current) {
          buttonRef = manageButtonRef.current
        } else if (activeTooltipButton === 'billing' && billingHelpRef.current) {
          buttonRef = billingHelpRef.current
        }
        
        if (!buttonRef) return null
        
        const rect = buttonRef.getBoundingClientRect()
        // Position tooltip above the button, horizontally centered
        const top = rect.top - 10
        const left = rect.left + (rect.width / 2)
        
        return (
          <div
            ref={subscriptionTooltipRef}
            className="fixed z-[10004] animate-fadeIn"
            style={{
              top: `${top}px`,
              left: `${left}px`,
              transform: 'translate(-50%, -100%)',
            }}
          >
            {/* Bubble with tail */}
            <div className="relative bg-[#3ECF8E] text-white px-4 py-2.5 rounded-lg shadow-lg mb-2 max-w-xs">
              <span className="text-sm font-medium">Vous êtes actuellement en période d'essai. Aucun paiement ne sera effectué pour le moment.</span>
              {/* Tail pointing down */}
              <div 
                className="absolute left-1/2 bottom-0 transform -translate-x-1/2 translate-y-full"
                style={{
                  width: 0,
                  height: 0,
                  borderLeft: '8px solid transparent',
                  borderRight: '8px solid transparent',
                  borderTop: '8px solid #3ECF8E',
                }}
              />
            </div>
          </div>
        )
      })()}
    </>
  )
}

export default Settings
